import { HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { ComponentStore } from '@ngrx/component-store';
import { toEventPlatformFeature } from '@utilities/event-payload-mapping.util';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';
import {
  TGlobalTemplateApi,
  TGlobalTemplateCreatePayload,
  TGlobalTemplateUpdatePayload,
} from './template-settings.interface';
import { TemplateSettingsService } from './template-settings.service';

interface ITemplateSettingsState {
  error: string | null;
  loading: boolean;
  templates: TGlobalTemplateApi[];
  templatesLoading: boolean;
}

const initialState: ITemplateSettingsState = {
  error: null,
  loading: false,
  templates: [],
  templatesLoading: false,
};

@Injectable()
export class TemplateSettingsStore extends ComponentStore<ITemplateSettingsState> {
  public readonly error$ = this.selectSignal((state: ITemplateSettingsState) => state.error);
  public readonly loading$ = this.selectSignal((state: ITemplateSettingsState) => state.loading);
  public readonly templates$ = this.selectSignal((state: ITemplateSettingsState) => state.templates);
  public readonly templatesLoading$ = this.selectSignal((state: ITemplateSettingsState) => state.templatesLoading);

  public readonly newTemplate$ = signal<TGlobalTemplateApi | null>(null);
  public readonly updatedTemplate$ = signal<TGlobalTemplateApi | null>(null);
  // LF-2 (audit 2026-08-06) - set on delete success so the component can fire
  // campaign_creation_global_group_removed_saved (the delete effect has no other success hook).
  public readonly deletedTemplate$ = signal<TGlobalTemplateApi | null>(null);

  private readonly handleError = (
    errorMessage: string,
    previousTemplates?: TGlobalTemplateApi[],
  ): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        if (previousTemplates) {
          this.patchState({
            templates: previousTemplates,
          });
        }

        this.patchState({
          error: errorMessage,
          loading: false,
          templatesLoading: false,
        });

        return EMPTY;
      }),
    );

  public readonly fetchGlobalTemplates = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() =>
        this.patchState({
          error: null,
          loading: true,
          templatesLoading: true,
        }),
      ),
      switchMap(() =>
        this.templateSettingsService
          .getGlobalTemplates(
            new HttpParams().set('order_by', 'platform_feature').set('limit', 1000).set('joins', 'all'),
          )
          .pipe(
            tap((templates: IApiResponse<TGlobalTemplateApi[]>) => {
              this.patchState({
                loading: false,
                templates: templates.data ?? [],
                templatesLoading: false,
              });
            }),
            this.handleError('Failed to load global templates'),
          ),
      ),
    );
  });

  public readonly createGlobalTemplate = this.effect(
    (template$: Observable<{ tempId: string; template: TGlobalTemplateCreatePayload }>) => {
      return template$.pipe(
        tap(() =>
          this.patchState({
            error: null,
            loading: true,
          }),
        ),
        switchMap(({ tempId, template }) => {
          const previousTemplates = this.get().templates;
          const optimisticTemplate: TGlobalTemplateApi = {
            ...template,
            id: tempId,
          };

          this.patchState({
            templates: [...previousTemplates, optimisticTemplate],
          });

          return this.templateSettingsService.createGlobalTemplate(template).pipe(
            tap((createdTemplate: TGlobalTemplateApi) => {
              this.patchState({
                loading: false,
                templates: this.get().templates.map((existingTemplate) =>
                  existingTemplate.id === tempId ? createdTemplate : existingTemplate,
                ),
              });
              this.newTemplate$.set(createdTemplate);
              this.eventTrackingService.track(EEventType.globalTemplateCreated, {
                platformFeature: toEventPlatformFeature(createdTemplate.platformFeature),
              });
            }),
            this.handleError('Failed to create global template', previousTemplates),
          );
        }),
      );
    },
  );

  public readonly updateGlobalTemplate = this.effect(
    (template$: Observable<{ id: string; template: TGlobalTemplateUpdatePayload }>) => {
      return template$.pipe(
        tap(() =>
          this.patchState({
            error: null,
            loading: true,
          }),
        ),
        switchMap(({ id, template }) => {
          const previousTemplates = this.get().templates;
          const optimisticTemplate: TGlobalTemplateApi = {
            ...template,
            id,
          };

          this.patchState({
            templates: previousTemplates.map((existingTemplate) =>
              existingTemplate.id === id ? optimisticTemplate : existingTemplate,
            ),
          });

          return this.templateSettingsService.updateGlobalTemplate(id, template).pipe(
            tap((updatedTemplate: TGlobalTemplateApi) => {
              this.patchState({
                loading: false,
                templates: this.get().templates.map((existingTemplate) =>
                  existingTemplate.id === id ? updatedTemplate : existingTemplate,
                ),
              });
              this.updatedTemplate$.set(updatedTemplate);
            }),
            this.handleError('Failed to update global template', previousTemplates),
          );
        }),
      );
    },
  );

  public readonly deleteGlobalTemplate = this.effect((templateId$: Observable<string>) => {
    return templateId$.pipe(
      tap(() =>
        this.patchState({
          error: null,
          loading: true,
        }),
      ),
      switchMap((id) => {
        const previousTemplates = this.get().templates;
        const deletedTemplate = previousTemplates.find((template) => template.id === id);

        this.patchState({
          templates: previousTemplates.filter((template) => template.id !== id),
        });

        return this.templateSettingsService.deleteGlobalTemplate(id).pipe(
          tap(() => {
            this.patchState({
              loading: false,
            });
            this.deletedTemplate$.set(deletedTemplate ?? null);
          }),
          this.handleError('Failed to delete global template', previousTemplates),
        );
      }),
    );
  });

  constructor(
    private readonly templateSettingsService: TemplateSettingsService,
    private readonly eventTrackingService: EventTrackingService,
  ) {
    super(initialState);
  }
}
